import React, { useState, useEffect } from 'react';
import { HashRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

// Public Pages
import LandingPage from './pages/public/LandingPage';
import FeaturesPage from './pages/public/FeaturesPage';
import PricingPage from './pages/public/PricingPage';
import AboutPage from './pages/public/AboutPage';
import ContactPage from './pages/public/ContactPage';
import SignInPage from './pages/auth/SignInPage';
import SignUpPage from './pages/auth/SignUpPage';

// Dashboard Pages
import Dashboard from './pages/dashboard/Dashboard';
import GalleryManager from './pages/dashboard/GalleryManager';
import PhotoEditor from './pages/dashboard/PhotoEditor';
import BookingManager from './pages/dashboard/BookingManager';
import StoreManager from './pages/dashboard/StoreManager';
import SettingsPage from './pages/dashboard/SettingsPage';
import WebsiteBuilder from './pages/dashboard/WebsiteBuilder';
import AnalyticsPage from './pages/dashboard/AnalyticsPage';

// Portfolio Site
import PortfolioSite from './pages/portfolio/PortfolioSite';

// Context
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { LanguageProvider } from './context/LanguageContext';

// Components
import LoadingSpinner from './components/common/LoadingSpinner';

function AppRoutes() {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <AnimatePresence mode="wait">
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/features" element={<FeaturesPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/signin" element={<SignInPage />} />
        <Route path="/signup" element={<SignUpPage />} />

        {/* Portfolio Routes */}
        <Route path="/portfolio/:username" element={<PortfolioSite />} />
        <Route path="/portfolio/:username/*" element={<PortfolioSite />} />

        {/* Protected Dashboard Routes */}
        <Route path="/dashboard" element={
          isAuthenticated ? <Dashboard /> : <Navigate to="/signin" />
        } />
        <Route path="/dashboard/galleries" element={
          isAuthenticated ? <GalleryManager /> : <Navigate to="/signin" />
        } />
        <Route path="/dashboard/editor" element={
          isAuthenticated ? <PhotoEditor /> : <Navigate to="/signin" />
        } />
        <Route path="/dashboard/bookings" element={
          isAuthenticated ? <BookingManager /> : <Navigate to="/signin" />
        } />
        <Route path="/dashboard/store" element={
          isAuthenticated ? <StoreManager /> : <Navigate to="/signin" />
        } />
        <Route path="/dashboard/website" element={
          isAuthenticated ? <WebsiteBuilder /> : <Navigate to="/signin" />
        } />
        <Route path="/dashboard/analytics" element={
          isAuthenticated ? <AnalyticsPage /> : <Navigate to="/signin" />
        } />
        <Route path="/dashboard/settings" element={
          isAuthenticated ? <SettingsPage /> : <Navigate to="/signin" />
        } />

        {/* Redirect to dashboard if authenticated, otherwise to home */}
        <Route path="*" element={
          <Navigate to={isAuthenticated ? "/dashboard" : "/"} />
        } />
      </Routes>
    </AnimatePresence>
  );
}

function App() {
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    // Initialize app
    const timer = setTimeout(() => {
      setIsInitialized(true);
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  if (!isInitialized) {
    return <LoadingSpinner />;
  }

  return (
    <ThemeProvider>
      <LanguageProvider>
        <AuthProvider>
          <Router>
            <div className="min-h-screen bg-white dark:bg-dark-900 text-gray-900 dark:text-white transition-colors duration-300">
              <AppRoutes />
            </div>
          </Router>
        </AuthProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;